import React, { useEffect } from 'react';
import { Select, SelectRootSlotProps, } from '@mui/base/Select';
import { Option as BaseOption, optionClasses } from '@mui/base/Option';
import { styled } from '@mui/system';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import {
  GRAY, GRAY_DARK, DISABLED_TEXT, GRAY_VERY_LIGHT, GREEN_BKG_OPT_SELECTED,
  GREEN_DARK, GREEN_TEXT_OPT_SELECTED
} from '../../../assets/Styles';
import { Typography } from '@mui/material';
import './index.css';
import { itemSelection } from '../../../models/itemSelection.model';

interface SimpleSelectionProps {
  label: string,
  options: itemSelection[],
  value: itemSelection | undefined,
  name: string,
  onChange: (p: any) => void,
  placeholder?: string,
  disabled?: boolean
}

const SimpleSelection: React.FC<SimpleSelectionProps> = ({
  label,
  options = [],
  value,
  name,
  onChange,
  placeholder,
  disabled
}) => {

  const [selected, setSelected] = React.useState<any>(value);

  useEffect(() => {
    if (value != null) {
      setSelected(value)
    }
  }, [value])

  const handleChange = (newValue: string) => {
    if (newValue != null) {
      const optSelected = options.find((el: any) => el.id === newValue)
      setSelected(optSelected)
      onChange({ 'target': { 'name': name, 'value': optSelected } })
    }
  }

  return (
    <div className='simple-selection-box'>
      <span className='simple-selection__label'>
        {(label) && <Label>{label}</Label>}
      </span>
      <Select
        slots={{
          root: Button,
          listbox: Listbox,
        }}
        slotProps={{
          popup: { className: 'CustomSelectIntroduction-popup' },
        }}
        disabled={disabled}
        value={selected ? selected.id : value}
        name={name}
        onChange={(_, newValue: string) => handleChange(newValue)}
        renderValue={
          selected !== '' ? undefined : () => <Typography color={'gray'} variant='body2'>{placeholder}</Typography>
        }
      >
        {(!disabled && (options === null || options.length === 0)) && (
          <Option disabled key={0} value={0}>{'Sin Opciones'}</Option>
        )}

        {(options != null && options.length > 0) && (
          options.map((opt: any) => (
            <Option key={opt.id} value={opt.id}>{opt.label}</Option>
          ))
        )}
      </Select>
    </div>
  );
}

const Button = React.forwardRef(function Button<
  TValue extends {},
  Multiple extends boolean,
>(
  props: SelectRootSlotProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { ownerState, disabled, ...other } = props;
  return (
    <StyledButton
      type="button" {...other} ref={ref}>
      {other.children}
      <UnfoldMoreRoundedIcon />
    </StyledButton>
  );
});

const StyledButton = styled('button', { shouldForwardProp: () => true })(
  ({ theme }) => `
  position: relative;
  font-size: 14px;
  box-sizing: border-box;
  min-width: 250px;
  padding: 8px 12px;
  border-radius: 6px;
  text-align: left;
  line-height: 1.5;
  background: #fff;
  border: 1px solid gray;
  color: ${GRAY_DARK};
  box-shadow: 0px 2px 6px ${'rgba(0,0,0, 0.05)'};
  cursor: pointer;

  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    border: 1px solid ${GREEN_DARK};
    box-shadow: 0 0 0 1px ${GREEN_DARK};
  }

  & > svg {
    font-size: 1rem;
    position: absolute;
    height: 100%;
    top: 0;
    right: 10px;
  }

  &.${optionClasses.disabled} {
    background-color: ${GRAY_VERY_LIGHT};
    color: ${DISABLED_TEXT};
  }
  `,
);

const Listbox = styled('ul')(
  ({ theme }) => `
  font-size: 14px;
  box-sizing: border-box;
  padding: 6px;
  margin: 6px 0;
  min-width: 260px;
  max-width: 320px;
  text-wrap;
  width: 100%;
  max-height: 400px;
  border-radius: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  outline: 0px;
  background-color: white;
  border: 1px solid ${GREEN_DARK};
  color: ${GRAY_DARK};
  cursor: pointer;
  box-shadow: 0px 2px 6px ${'rgba(0,0,0, 0.05)'};
  `,
);

const Option = styled(BaseOption)(
  ({ theme }) => `
  list-style: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;

  &:last-of-type {
    border-bottom: none;
  }

  &.${optionClasses.selected} {
    background-color: ${GREEN_BKG_OPT_SELECTED};
    color: ${GREEN_TEXT_OPT_SELECTED};
  }

  &.${optionClasses.highlighted} {
    background-color: ${GRAY_VERY_LIGHT};
    color: ${GRAY_DARK};
  }

  &.${optionClasses.highlighted}.${optionClasses.selected} {
    background-color: ${GREEN_BKG_OPT_SELECTED};
    color: ${GREEN_DARK};
  }

  &.${optionClasses.disabled} {
    color: ${GRAY};
  }

  &:hover:not(.${optionClasses.disabled}) {
    background-color: ${GRAY_VERY_LIGHT};
    color: ${GRAY_DARK};
  }
  `,
);

const Label = styled("label")(
  ({ theme }) => `
    font-size: 12px;
    display: block;
    margin-bottom: 4px;
    font-weight: 400;
    color: ${GRAY_DARK};
    `
);

export default SimpleSelection;